import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { DeliverDialog } from "../components/DeliverDialog";
import { StarRating } from "../components/StarRating";
import { VEHICLE_TYPES, JOB_CATEGORIES, REVIEW_CRITERIA, TRANSPORT_SERVICES, COUNTRIES, jobStatusLabel, jobStatusColor } from "../lib/constants";
import { MapPin, Package, Truck, AlertTriangle, Upload, Calendar, Boxes, FileCheck, Play, CheckCircle2, Inbox } from "lucide-react";

export default function DriverDashboard() {
  const { user, checkAuth } = useAuth();
  const approved = user.status === "approved";
  const [open, setOpen] = useState([]);
  const [apps, setApps] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({ truck_type: "all", job_category: "all" });

  const reloadJobs = useCallback(async () => {
    if (!approved) return;
    const params = {};
    if (filters.truck_type !== "all") params.truck_type = filters.truck_type;
    if (filters.job_category !== "all") params.job_category = filters.job_category;
    const r = await api.get("/transport-jobs", { params });
    setOpen(r.data);
  }, [approved, filters]);

  const reloadMine = useCallback(async () => {
    const [a, j, l] = await Promise.all([api.get("/driver/applications"), api.get("/driver/jobs"), api.get("/driver/leads")]);
    setApps(a.data); setAssigned(j.data); setLeads(l.data);
  }, []);

  useEffect(() => { reloadJobs(); }, [reloadJobs]);
  useEffect(() => { reloadMine(); }, [reloadMine]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="label-caps text-accent">Transporter console</span>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">{user.company_name || user.name}</h1>
          </div>
          <Badge className={`rounded-none uppercase tracking-wider ${approved ? "bg-accent text-foreground" : "bg-white/20 text-white"}`} data-testid="driver-status">{user.status}</Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {!approved && (
          <div className="border border-accent bg-accent/10 p-4 flex items-start gap-3 mb-6" data-testid="pending-banner">
            <AlertTriangle className="h-5 w-5 text-foreground shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Your transporter account is pending approval</p>
              <p className="text-sm text-muted-foreground">Upload your insurance & vehicle documents under "Profile & Docs". Admin must approve them before you can apply for jobs.</p>
            </div>
          </div>
        )}

        <Tabs defaultValue={approved ? "find" : "profile"}>
          <TabsList className="rounded-none bg-muted border border-border flex-wrap h-auto">
            {approved && <TabsTrigger value="find" className="rounded-none" data-testid="tab-find-jobs">Find Jobs ({open.length})</TabsTrigger>}
            <TabsTrigger value="applications" className="rounded-none" data-testid="tab-applications">My Applications ({apps.length})</TabsTrigger>
            <TabsTrigger value="assigned" className="rounded-none" data-testid="tab-assigned">Assigned ({assigned.length})</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-none" data-testid="tab-leads">Quote Requests ({leads.length})</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-none" data-testid="tab-reviews">My Ratings & Reviews</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-none" data-testid="tab-profile">Profile & Docs</TabsTrigger>
          </TabsList>

          {approved && (
            <TabsContent value="find" className="mt-6">
              <div className="flex flex-wrap gap-3 mb-4">
                <Select value={filters.truck_type} onValueChange={(v) => setFilters({ ...filters, truck_type: v })}>
                  <SelectTrigger className="rounded-none w-44 h-10" data-testid="filter-truck-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vehicle types</SelectItem>
                    {VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.job_category} onValueChange={(v) => setFilters({ ...filters, job_category: v })}>
                  <SelectTrigger className="rounded-none w-56 h-10" data-testid="filter-job-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All job types</SelectItem>
                    {JOB_CATEGORIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {open.length === 0 ? <Empty text="No open jobs match your filters. Check back soon." /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {open.map((j) => <JobCard key={j.job_id} job={j} onApplied={() => { reloadJobs(); reloadMine(); }} />)}
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="applications" className="mt-6">
            {apps.length === 0 ? <Empty text="You haven't applied to any jobs yet." /> : (
              <div className="border border-border divide-y divide-border">
                {apps.map((a) => (
                  <div key={a.application_id} className="p-4 flex items-center justify-between gap-3" data-testid={`application-${a.application_id}`}>
                    <div>
                      <div className="font-medium">{a.job_title}</div>
                      <div className="text-xs text-muted-foreground">Quote: {a.quote ? `£${a.quote}` : "—"}{a.message ? ` · ${a.message}` : ""}</div>
                    </div>
                    <Badge className={`rounded-none text-[10px] uppercase ${a.status === "accepted" ? "bg-primary text-white" : a.status === "rejected" ? "bg-destructive text-white" : "bg-accent text-foreground"}`}>{a.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="assigned" className="mt-6">
            {assigned.length === 0 ? <Empty text="No jobs assigned to you yet." /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assigned.map((j) => <JobCard key={j.job_id} job={j} assignedView onChange={reloadMine} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            {leads.length === 0 ? <Empty text="No quote requests yet. Companies who find you in the directory will appear here." /> : (
              <div className="border border-border divide-y divide-border">
                {leads.map((l) => (
                  <div key={l.lead_id} className="p-4" data-testid={`lead-${l.lead_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium flex items-center gap-2"><Inbox className="h-4 w-4 text-primary" /> {l.from_name}</span>
                      <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</span>
                    </div>
                    {(l.pickup_location || l.delivery_location) && <p className="text-sm text-muted-foreground mt-1">{[l.pickup_location, l.delivery_location].filter(Boolean).join(" → ")}</p>}
                    <p className="text-sm mt-2 whitespace-pre-line">{l.message}</p>
                    {l.contact_back && <p className="text-xs text-primary mt-2">Reply via: {l.contact_back}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <MyReviews />
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <ProfileDocs user={user} onSaved={checkAuth} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function JobCard({ job, onApplied, assignedView, onChange }) {
  const pod = job.proof_of_delivery;
  const start = async () => {
    try {
      await api.post(`/transport-jobs/${job.job_id}/start`);
      toast.success("Job started — marked In Progress");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  return (
    <div className="border border-border p-5" data-testid={`open-job-${job.job_id}`}>
      <div className="flex items-center justify-between mb-2">
        <Truck className="h-5 w-5 text-primary" />
        <Badge className={`rounded-none text-[10px] uppercase ${jobStatusColor(job.status)}`} data-testid={`job-badge-${job.job_id}`}>{jobStatusLabel(job)}</Badge>
      </div>
      <h3 className="font-display font-bold text-lg tracking-tight">{job.title}</h3>
      {job.job_category && <span className="label-caps text-primary">{job.job_category}</span>}
      <div className="space-y-1 mt-3 text-sm">
        <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> {job.pickup_location} → {job.delivery_location}</div>
        {job.truck_type && <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {job.truck_type}{job.loading_help ? " · loading help" : ""}</div>}
        {(job.goods_type || job.weight || job.pallets) && <div className="flex items-center gap-2"><Boxes className="h-3.5 w-3.5 text-muted-foreground" /> {[job.goods_type, job.weight, job.pallets && `${job.pallets} pallets`].filter(Boolean).join(" · ")}</div>}
        {(job.pickup_datetime || job.delivery_deadline) && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-muted-foreground" /> {[job.pickup_datetime, job.delivery_deadline].filter(Boolean).join(" → ")}</div>}
        <div className="flex items-center gap-2 font-medium"><Package className="h-3.5 w-3.5 text-muted-foreground" /> {job.request_quote ? "Request a quote" : job.price ? `£${job.price}` : "—"}</div>
      </div>
      {job.description && <p className="text-sm text-muted-foreground mt-2">{job.description}</p>}
      <p className="text-xs text-muted-foreground mt-2">Posted by {job.poster_name}</p>

      {pod && (
        <div className="mt-3 bg-muted p-2 text-xs flex items-center gap-3" data-testid={`pod-${job.job_id}`}>
          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
          <span>Proof of delivery: {pod.note || "—"}</span>
          {pod.photo && <a href={fileUrl(pod.photo)} target="_blank" rel="noopener noreferrer" className="text-primary underline">photo</a>}
        </div>
      )}

      {assignedView ? (
        <div className="flex gap-2 mt-4 flex-wrap">
          {job.status === "assigned" && (
            <Button onClick={start} className="rounded-none bg-primary hover:bg-primary/90" size="sm" data-testid={`start-job-${job.job_id}`}><Play className="h-4 w-4 mr-1" /> Start job</Button>
          )}
          {(job.status === "assigned" || job.status === "in_progress") && (
            <DeliverDialog jobId={job.job_id} onDone={onChange} />
          )}
          {job.status === "delivered" && <p className="text-xs text-muted-foreground self-center">Delivered — waiting for the company to confirm completion.</p>}
          {job.status === "completed" && <p className="text-xs text-primary font-medium self-center flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Completed</p>}
        </div>
      ) : (
        <ApplyDialog job={job} onApplied={onApplied} />
      )}
    </div>
  );
}

function ApplyDialog({ job, onApplied }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quote: "", message: "", contact: "" });
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    setSaving(true);
    try {
      await api.post(`/transport-jobs/${job.job_id}/apply`, {
        quote: form.quote ? parseFloat(form.quote) : null, message: form.message, contact: form.contact,
      });
      toast.success("Quote submitted");
      setOpen(false);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally {
      setSaving(false);
    }
  };

  if (job.already_applied) {
    return <Button disabled className="rounded-none w-full mt-4" data-testid={`applied-${job.job_id}`}>Quote submitted</Button>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-primary hover:bg-primary/90 w-full mt-4" data-testid={`apply-job-${job.job_id}`}>Apply with quote</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none">
        <DialogHeader><DialogTitle className="font-display">Apply: {job.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="label-caps">Your quote (£)</Label><Input type="number" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="rounded-none mt-1 h-10" data-testid="apply-quote" /></div>
          <div><Label className="label-caps">Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="rounded-none mt-1" placeholder="Availability, vehicle, ETA…" data-testid="apply-message" /></div>
          <div><Label className="label-caps">Contact (phone / WhatsApp)</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="rounded-none mt-1 h-10" data-testid="apply-contact" /></div>
        </div>
        <DialogFooter>
          <Button onClick={apply} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90" data-testid="submit-application-btn">{saving ? "Submitting…" : "Submit quote"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyReviews() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const r = await api.get(`/transporters/${user.user_id}`);
    setData(r.data);
    setLoading(false);
  }, [user.user_id]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <Empty text="Loading your ratings…" />;
  const { reviews = [], averages = {}, reviews_count = 0, completed_jobs = 0 } = data;

  return (
    <div className="space-y-6" data-testid="my-reviews">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-foreground bg-foreground text-white p-5">
          <span className="label-caps text-accent">Average rating</span>
          <div className="flex items-center gap-3 mt-2">
            <span className="font-display font-black text-4xl" data-testid="avg-rating">{averages.overall ?? "—"}</span>
            <StarRating value={averages.overall || 0} />
          </div>
          <div className="flex gap-4 text-xs text-white/60 mt-2">
            <span data-testid="reviews-count">{reviews_count} review{reviews_count === 1 ? "" : "s"}</span>
            <span data-testid="completed-jobs">{completed_jobs} completed job{completed_jobs === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="border border-border p-5 md:col-span-2">
          <span className="label-caps text-primary">Category breakdown</span>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3">
            {REVIEW_CRITERIA.filter((c) => c.key !== "overall").map((c) => (
              <div key={c.key} className="flex items-center justify-between text-sm" data-testid={`avg-${c.key}`}>
                <span className="text-muted-foreground">{c.label}</span>
                <span className="font-medium flex items-center gap-2">{averages[c.key] ?? "—"} <StarRating value={averages[c.key] || 0} size="h-3.5 w-3.5" /></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {reviews.length === 0 ? <Empty text="No reviews yet. Complete jobs to receive ratings." /> : (
        <div className="border border-border divide-y divide-border">
          {reviews.map((r) => <ReviewItem key={r.review_id} review={r} onReplied={load} />)}
        </div>
      )}
    </div>
  );
}

function ReviewItem({ review, onReplied }) {
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      await api.post(`/reviews/${review.review_id}/reply`, { reply });
      toast.success("Reply added");
      setShow(false);
      onReplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reply"); } finally { setSaving(false); }
  };

  return (
    <div className="p-4" data-testid={`review-${review.review_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm">{review.company_name} <span className="text-muted-foreground">· {review.job_title}</span></div>
        <span className="flex items-center gap-2 text-sm"><StarRating value={review.overall} size="h-4 w-4" /> {review.overall}</span>
      </div>
      {review.comment && <p className="text-sm text-muted-foreground mt-2">{review.comment}</p>}
      {review.reply ? (
        <div className="mt-3 bg-muted p-3 text-sm" data-testid={`review-reply-${review.review_id}`}><span className="label-caps text-primary">Your reply</span><p className="mt-1">{review.reply}</p></div>
      ) : show ? (
        <div className="mt-3 flex gap-2">
          <Input value={reply} onChange={(e) => setReply(e.target.value)} className="rounded-none h-9" placeholder="Write a public reply…" data-testid={`reply-input-${review.review_id}`} />
          <Button onClick={submit} disabled={saving} size="sm" className="rounded-none bg-primary hover:bg-primary/90" data-testid={`submit-reply-${review.review_id}`}>{saving ? "…" : "Reply"}</Button>
        </div>
      ) : (
        <Button onClick={() => setShow(true)} variant="outline" size="sm" className="rounded-none mt-3" data-testid={`reply-btn-${review.review_id}`}>Reply</Button>
      )}
    </div>
  );
}

function ProfileDocs({ user, onSaved }) {
  const [form, setForm] = useState({
    company_name: user.company_name || "", phone: user.phone || "", whatsapp: user.whatsapp || "",
    vehicle_type: user.vehicle_type || "", service_areas: user.service_areas || "", routes: user.routes || "",
    country: user.country || "", bio: user.bio || "",
    public_profile: user.public_profile !== false,
    job_capabilities: user.job_capabilities || [],
  });
  const [docs, setDocs] = useState(user.documents || {});
  const [saving, setSaving] = useState(false);

  const toggleCap = (c) => setForm((p) => ({
    ...p, job_capabilities: p.job_capabilities.includes(c) ? p.job_capabilities.filter((x) => x !== c) : [...p.job_capabilities, c],
  }));

  const uploadDoc = (key) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...docs, [key]: data.path };
      setDocs(next);
      await api.put("/me/profile", { documents: next });
      toast.success("Document uploaded");
      onSaved && onSaved();
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", { ...form, documents: docs });
      toast.success("Profile saved");
      onSaved && onSaved();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const DocRow = ({ label, k, isImage }) => (
    <div className="flex items-center justify-between gap-3 border border-border p-3" data-testid={`doc-${k}`}>
      <div className="flex items-center gap-2">
        <FileCheck className={`h-4 w-4 ${docs[k] ? "text-primary" : "text-muted-foreground"}`} />
        <span className="text-sm font-medium">{label}</span>
        {docs[k] && (isImage
          ? <img src={fileUrl(docs[k])} alt={label} className="h-10 w-10 object-cover border border-border" />
          : <a href={fileUrl(docs[k])} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">view</a>)}
      </div>
      <label className="cursor-pointer">
        <Button asChild size="sm" variant="outline" className="rounded-none pointer-events-none"><span><Upload className="h-4 w-4 mr-1" />{docs[k] ? "Replace" : "Upload"}</span></Button>
        <input type="file" className="hidden" onChange={uploadDoc(k)} data-testid={`upload-${k}`} />
      </label>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-border p-5">
        <h3 className="font-display font-bold text-lg mb-4">Company & vehicle</h3>
        <div className="space-y-3">
          <div><Label className="label-caps">Company name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-none mt-1 h-10" data-testid="profile-company" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1 h-10" data-testid="profile-phone" /></div>
            <div><Label className="label-caps">WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="rounded-none mt-1 h-10" data-testid="profile-whatsapp" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Vehicle type</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="profile-vehicle"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Country</Label>
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="profile-country"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="label-caps">Service areas</Label><Input value={form.service_areas} onChange={(e) => setForm({ ...form, service_areas: e.target.value })} className="rounded-none mt-1 h-10" data-testid="profile-areas" /></div>
          <div><Label className="label-caps">Routes</Label><Input value={form.routes} onChange={(e) => setForm({ ...form, routes: e.target.value })} className="rounded-none mt-1 h-10" data-testid="profile-routes" /></div>
          <div><Label className="label-caps">Short profile description</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={2} className="rounded-none mt-1" placeholder="Tell companies about your fleet and experience…" data-testid="profile-bio" /></div>
          <div>
            <Label className="label-caps">Services offered</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {TRANSPORT_SERVICES.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.job_capabilities.includes(c)} onCheckedChange={() => toggleCap(c)} data-testid={`cap-${c.replace(/\s+/g, "-").toLowerCase()}`} /> {c}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border border-border p-3" data-testid="public-profile-row">
            <div>
              <p className="text-sm font-medium">Show my transporter profile publicly</p>
              <p className="text-xs text-muted-foreground">Appear in the public "Find a Transporter" directory once admin-verified.</p>
            </div>
            <Switch checked={form.public_profile} onCheckedChange={(v) => setForm({ ...form, public_profile: v })} data-testid="public-profile-toggle" />
          </div>
          <Button onClick={save} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90 w-full" data-testid="save-profile-btn">{saving ? "Saving…" : "Save profile"}</Button>
        </div>
      </div>
      <div className="border border-border p-5">
        <h3 className="font-display font-bold text-lg mb-1">Verification documents</h3>
        <p className="text-xs text-muted-foreground mb-4">Required before admin can approve you for jobs. These files are never shown publicly.</p>
        <div className="space-y-3">
          <DocRow label="Vehicle photo" k="vehicle_photo" isImage />
          <DocRow label="Insurance document" k="insurance" />
          <DocRow label="Vehicle registration" k="registration" />
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="empty-state">{text}</div>;
}
